import {
  Box,
  Container,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
// import React from "react";
import { motion } from "framer-motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
import HeroBanner from "../components/HeroBanner";
import ServiceCard from "../components/ServiceCard";
import FeaturesGrid from "../components/FeaturesGrid";
import TestimonialCarousel from "../components/TestimonialCarousel";
import GoogleReviews from "../components/GoogleReviews";
import ContactForm from "../components/ContactForm";
import OfferCountdown from "../components/OfferCountdown";
import FloatingChat from "../components/FloatingChat";
import GalleryPreview from "../components/GalleryPreview";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";

const services = [
  {
    title: "Post Construction Cleaning",
    description:
      "Transform your construction site into a pristine living space. Our specialized team removes all construction debris, dust, and residues, ensuring every corner is spotless and safe for occupancy. Final touch cleaning for your dream space.",
    image: "/images/clean2.JPG",
    link: "/services/post-construction",
    icon: "🏗️",
    features: ["Debris Removal", "Deep Dusting", "Final Touch Polish"],
    badge: "Most Popular",
    gradient: "linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)",
  },
  {
    title: "Premium Deep Cleaning",
    description:
      "Experience the ultimate clean with our intensive deep cleaning service. We tackle hidden dirt, sanitize high-touch areas, and refresh your entire space. Perfect for seasonal cleaning or pre-event preparation.",
    image: "/images/clean4.JPG",
    link: "/services/deep-cleaning",
    icon: "✨",
    features: ["Sanitization", "Grime Removal", "Air Quality Improvement"],
    badge: "Eco-Friendly",
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },
  {
    title: "Professional Fumigation",
    description:
      "Complete pest elimination with our safe and effective fumigation services. We use environmentally friendly solutions to protect your family and pets while eliminating pests completely. Your comfort is our priority.",
    image: "/images/fumigation.JPG",
    link: "/services/fumigation",
    icon: "🐛",
    features: [
      "Eco-Friendly Solutions",
      "Complete Pest Removal",
      "Safe for Families",
    ],
    badge: "Certified",
    gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
  },
  {
    title: "Trusted Maid Services",
    description:
      "Reliable, vetted domestic professionals for your home. Our maids undergo rigorous background checks and professional training. Regular cleaning, organization, and maintenance tailored to your schedule.",
    image:
      "https://images.unsplash.com/photo-1581578949510-fa7315c4c350?w=800&q=80",
    link: "/services/hiring-maids",
    icon: "👩‍💼",
    features: [
      "Background Verified",
      "Trained Professionals",
      "Flexible Scheduling",
    ],
    badge: "Verified",
    gradient: "linear-gradient(135deg, #FC466B 0%, #3F5EFB 100%)",
  },
  {
    title: "Advanced Vacuum Cleaning",
    description:
      "Revitalize your upholstery and carpets with our industrial-grade vacuum systems. Remove deep-seated allergens, dust mites, and stubborn dirt for a healthier living environment and fresh-looking furniture.",
    image: "/images/chair.jpeg",
    link: "/services/vacuum-cleaning",
    icon: "🧹",
    features: ["Allergen Removal", "Stain Treatment", "Fabric Protection"],
    badge: "Health Focus",
    gradient: "linear-gradient(135deg, #fd746c 0%, #ff9068 100%)",
  },
  {
    title: "Luxury Laundry Care",
    description:
      "Premium laundry services with pickup and delivery. We handle your garments with care using premium detergents and precise techniques. From everyday wear to delicate items, we've got you covered.",
    image:
      "https://images.unsplash.com/photo-1582735689369-4fe89db7114c?w=800&q=80",
    link: "/services/laundry-from-homes",
    icon: "👔",
    features: ["Pickup & Delivery", "Premium Detergents", "Garment Care"],
    badge: "Convenient",
    gradient: "linear-gradient(135deg, #5ee7df 0%, #b490ca 100%)",
  },
  {
    title: "Event Cleaning Services",
    description:
      "Event cleaning management includes pre-event deep cleaning, on-site trash removal and restroom maintenance during the event, and post-event deep cleaning to restore the venue. We provide flexible scheduling and rapid response for on-site emergencies.",
    image: "/images/events.png",
    link: "/services/event-cleaning",
    icon: "🎉",
    features: [
      "Pre-event deep cleaning",
      "On-site trash & restroom maintenance",
      "Post-event restoration",
      "Flexible scheduling & emergency response",
    ],
    badge: "Event Ready",
    gradient: "linear-gradient(135deg, #f6d365 0%, #fda085 100%)",
  },
  {
    title: "Office & Commercial Cleaning",
    description:
      "Professional cleaning solutions for businesses of all sizes. We work around your schedule to maintain a clean, productive work environment. Custom packages available for offices, retail spaces, and commercial properties.",
    image:
      "https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=800&q=80",
    link: "/services/commercial-cleaning",
    icon: "🏢",
    features: [
      "After-Hours Service",
      "Custom Packages",
      "Commercial Grade Equipment",
    ],
    badge: "Business Ready",
    gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
  },
  {
    title: "Eco-Friendly Cleaning",
    description:
      "Green cleaning solutions that are tough on dirt but gentle on the planet. Using non-toxic, biodegradable products to create a healthy environment for your family and our ecosystem.",
    image:
      "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=800&q=80",
    link: "/services/eco-cleaning",
    icon: "🌿",
    features: ["Non-Toxic Products", "Biodegradable", "Child & Pet Safe"],
    badge: "Green Certified",
    gradient: "linear-gradient(135deg, #0ba360 0%, #3cba92 100%)",
  },
  {
    title: "Move In/Out Cleaning",
    description:
      "Stress-free transition cleaning for movers. We ensure your old property is spotless for the next occupants and your new home is perfectly clean and ready for your fresh start.",
    image:
      "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80",
    link: "/services/move-cleaning",
    icon: "🚚",
    features: [
      "Complete Property Clean",
      "Appliance Cleaning",
      "Wall & Floor Care",
    ],
    badge: "Stress-Free",
    gradient: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
  },

  {
    title: "Cleaner Training & Certification",
    description:
      "Comprehensive training programs that transform beginners into professional cleaners. Our certified graduates are equipped with industry-best practices, safety protocols, and advanced cleaning techniques.",
    image: "/images/clean5.JPG",
    link: "/services/training-certifying",
    icon: "🎓",
    features: [
      "Hands-On Practical Training",
      "Eco-Friendly Cleaning Methods",
      "Safety & Compliance Standards",
    ],
    badge: "Professional",
    gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
  },
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const floatingAnimation = {
  y: [-10, 10, -10],
  transition: {
    duration: 4,
    repeat: Infinity,
    ease: "easeInOut",
  },
};

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <>
      <SEO
        title="Blossom Cleaning City Services - Professional Cleaning in Kampala | Post-Construction, Deep Cleaning & More"
        description="Leading cleaning services in Kampala, Uganda. Post-construction cleaning, deep cleaning, fumigation, professional maid services & more. Trusted by 500+ clients in Kololo & across Kampala. Call 0752 058 190 for a free quote."
        keywords="cleaning services Kampala, post construction cleaning Uganda, deep cleaning services Kampala, fumigation services Kampala, maid services Uganda, professional cleaners Kampala, office cleaning Uganda, home cleaning Kampala, commercial cleaning services, residential cleaning Kampala, Kololo cleaning services, best cleaning company Uganda"
        url="https://blossomcleaningservices.com/"
      />
      <StructuredData />
      <Header />

      {/* Hero Section */}
      <HeroBanner />
      <OfferCountdown />
      {/* Services Section */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          backgroundColor: "background.default",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background Elements */}
        <motion.div
          style={{
            position: "absolute",
            top: "10%",
            left: "5%",
            width: 100,
            height: 100,
            borderRadius: "50%",
            background: "linear-gradient(45deg, #e3f2fd, #bbdefb)",
            opacity: 0.6,
          }}
          animate={floatingAnimation}
        />
        <motion.div
          style={{
            position: "absolute",
            bottom: "15%",
            right: "7%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(45deg, #f3e5f5, #e1bee7)",
            opacity: 0.4,
          }}
          animate={{
            ...floatingAnimation,
            transition: { ...floatingAnimation.transition, delay: 1 },
          }}
        />

        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant={isMobile ? "h3" : "h2"}
              align="center"
              sx={{
                mb: 2,
                fontWeight: 800,
                background: "linear-gradient(45deg, #1976d2, #42a5f5)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Our Services
            </Typography>
            <Typography
              variant={isMobile ? "body1" : "h6"}
              align="center"
              color="text.secondary"
              sx={{
                mb: 8,
                maxWidth: 700,
                mx: "auto",
                fontSize: { xs: "1rem", md: "1.25rem" },
              }}
            >
              Professional cleaning solutions tailored to your needs
            </Typography>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            <Grid
              container
              spacing={3}
              // style={{
              //   display: "flex",
              //   backgroundColor: "red",
              //   alignItems: "stretch",
              // }}
            >
              {services.map((service, index) => (
                <Grid item xs={12} sm={6} lg={4} key={index}>
                  <motion.div
                    variants={itemVariants}
                    whileHover={{
                      y: -8,
                      transition: { duration: 0.3 },
                    }}
                  >
                    <ServiceCard
                      {...service}
                      elevation={3}
                      sx={{
                        height: "100%",
                        transition: "all 0.3s ease",

                        "&:hover": {
                          boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                          transform: "translateY(-8px)",
                        },
                      }}
                    />
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Features Section */}
      <FeaturesGrid />

      {/* How It Works */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          backgroundColor: "background.paper",
          position: "relative",
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant={isMobile ? "h3" : "h2"}
              align="center"
              sx={{
                mb: 6,
                fontWeight: 800,
                background: "linear-gradient(45deg, #2e7d32, #4caf50)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              How It Works
            </Typography>
          </motion.div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 29,
            }}
          >
            {[
              {
                step: "1",
                title: "Book",
                description:
                  "Choose your service and schedule a convenient time online or by phone",
                icon: "📅",
              },
              {
                step: "2",
                title: "We Clean",
                description:
                  "Our professional team arrives on time and delivers exceptional cleaning",
                icon: "🧽",
              },
              {
                step: "3",
                title: "Relax",
                description:
                  "Enjoy your spotless space with complete peace of mind and satisfaction",
                icon: "😊",
              },
            ].map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30, scale: 0.9 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.6,
                    delay: index * 0.2,
                    type: "spring",
                    stiffness: 100,
                  }}
                  whileHover={{
                    scale: 1.05,
                    transition: { duration: 0.3 },
                  }}
                >
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 4,
                      borderRadius: 4,
                      backgroundColor: "background.default",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                      height: "100%",
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      },
                    }}
                  >
                    <motion.div
                      animate={{
                        y: [0, -10, 0],
                        rotate: [0, 5, 0],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: index * 0.5,
                      }}
                    >
                      <Typography
                        variant="h2"
                        sx={{
                          mb: 2,
                          fontSize: { xs: "3rem", md: "4rem" },
                        }}
                      >
                        {item.icon}
                      </Typography>
                    </motion.div>

                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        backgroundColor: "primary.main",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        mx: "auto",
                        mb: 3,
                        boxShadow: "0 4px 15px rgba(25, 118, 210, 0.3)",
                      }}
                    >
                      {item.step}
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        mb: 2,
                        fontWeight: 700,
                        color: "primary.main",
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.6,
                        fontSize: { xs: "0.9rem", md: "1rem" },
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </div>
        </Container>
      </Box>

      {/* Testimonials */}
      <TestimonialCarousel />

      {/* Google Reviews */}
      <GoogleReviews />

      {/* Gallery Preview */}
      <GalleryPreview />

      {/* Contact Form */}
      <Box
        sx={{
          // py: { xs: 6, md: 10 },
          backgroundColor: "background.default",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated background elements */}
        <motion.div
          style={{
            position: "absolute",
            top: "20%",
            left: "10%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "linear-gradient(45deg, #ffebee, #ffcdd2)",
            opacity: 0.3,
          }}
          animate={floatingAnimation}
        />
        <motion.div
          style={{
            position: "absolute",
            bottom: "30%",
            right: "15%",
            width: 100,
            height: 100,
            borderRadius: "30%",
            background: "linear-gradient(45deg, #e8f5e8, #c8e6c9)",
            opacity: 0.3,
            rotate: 45,
          }}
          animate={{
            ...floatingAnimation,
            transition: { ...floatingAnimation.transition, delay: 2 },
          }}
        />

        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <ContactForm
              title="Request a Quote"
              sx={{
                boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
                borderRadius: 4,
                overflow: "hidden",
              }}
            />
          </motion.div>
        </Container>
      </Box>
      <FloatingChat />
      <Footer />
    </>
  );
};

export default Home;
